import {
  ACESFilmicToneMapping,
  type Camera,
  HalfFloatType,
  PCFShadowMap,
  PMREMGenerator,
  type Line,
  type Material,
  type Mesh,
  type Object3D,
  type Points,
  type Sprite,
  type PerspectiveCamera,
  type Texture,
  type Scene,
  Vector2,
  WebGLRenderer,
  WebGLRenderTarget,
} from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { onSettings, settings } from '../settings';
import { RIM } from './materials';

/** Korekcja kolorów na końcu: nasycenie, kontrast, ciepło cieni i świateł, winieta. */
export interface Grade {
  saturation: number;
  contrast: number;
  /** >0 ociepla jasne partie i ochładza cienie (typowy „filmowy” podział tonów). */
  warmth: number;
  vignette: number;
  bloom: number;
}

export const DEFAULT_GRADE: Grade = { saturation: 1.15, contrast: 1.06, warmth: 0.5, vignette: 0.35, bloom: 0.45 };

export interface StageScene {
  readonly scene: Scene;
  readonly camera: Camera;
  readonly grade?: Grade;
  /** Kolor i siła światła konturowego postaci w tej scenie. */
  readonly rim?: { color: string; strength: number };
  resize(width: number, height: number): void;
  update(dt: number): void;
}

const GradeShader = {
  uniforms: {
    tDiffuse: { value: null },
    uSaturation: { value: 1 },
    uContrast: { value: 1 },
    uWarmth: { value: 0 },
    uVignette: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uSaturation, uContrast, uWarmth, uVignette;
    varying vec2 vUv;
    void main() {
      vec3 c = texture2D(tDiffuse, vUv).rgb;
      float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
      c = mix(vec3(l), c, uSaturation);
      c = (c - 0.5) * uContrast + 0.5;
      // Split toning: ciepłe światła, chłodne cienie.
      c += uWarmth * 0.06 * (smoothstep(0.4, 1.0, l) * vec3(1.0, 0.55, 0.1) - (1.0 - smoothstep(0.0, 0.6, l)) * vec3(0.25, 0.1, -0.35));
      float d = distance(vUv, vec2(0.5));
      c *= 1.0 - uVignette * smoothstep(0.35, 0.85, d);
      gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
    }`,
};

/**
 * Bezpiecznik: zamienia NaN i nieskończoności na czerń w pojedynczym pikselu i obcina skrajne jasności.
 * Bez tego jeden zły piksel z dowolnego shadera poświata rozmazuje na cały ekran (czarna klatka).
 */
const SanitizeShader = {
  uniforms: { tDiffuse: { value: null } },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    varying vec2 vUv;
    void main() {
      vec4 c = texture2D(tDiffuse, vUv);
      if (any(isnan(c)) || any(isinf(c))) c = vec4(0.0, 0.0, 0.0, 1.0);
      gl_FragColor = vec4(min(c.rgb, vec3(64.0)), c.a);
    }`,
};

/**
 * Styl obrazu:
 *  wyraźny (uMode 1) — wyostrzenie i cienkie kontury brył; ostre krawędzie bez rozmycia,
 *  malowany (uMode 2) — filtr Kuwahary (plamy farby) i mocniejsze kontury.
 * Styl „czysty” wyłącza ten pass całkiem.
 */
const PaintShader = {
  defines: { RADIUS: 4 },
  uniforms: {
    tDiffuse: { value: null },
    uTexel: { value: new Vector2(1 / 1024, 1 / 1024) },
    uMode: { value: 1 },
    uStrength: { value: 0.85 },
    uInk: { value: 0.35 },
    uSharpen: { value: 0.45 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 uTexel;
    uniform int uMode;
    uniform float uStrength, uInk, uSharpen;
    varying vec2 vUv;

    float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

    void quadrant(int x0, int y0, inout vec3 mean, inout float variance) {
      vec3 sum = vec3(0.0);
      vec3 sq = vec3(0.0);
      for (int j = 0; j <= RADIUS; j++) {
        for (int i = 0; i <= RADIUS; i++) {
          vec3 c = texture2D(tDiffuse, vUv + vec2(float(x0 + i), float(y0 + j)) * uTexel).rgb;
          sum += c;
          sq += c * c;
        }
      }
      float n = float((RADIUS + 1) * (RADIUS + 1));
      mean = sum / n;
      vec3 v = abs(sq / n - mean * mean);
      variance = v.r + v.g + v.b;
    }

    void main() {
      vec3 orig = texture2D(tDiffuse, vUv).rgb;
      vec3 c = orig;
      if (uMode == 2) {
        vec3 m0, m1, m2, m3; float v0, v1, v2, v3;
        quadrant(-RADIUS, -RADIUS, m0, v0);
        quadrant(0, -RADIUS, m1, v1);
        quadrant(-RADIUS, 0, m2, v2);
        quadrant(0, 0, m3, v3);
        vec3 paint = m0; float best = v0;
        if (v1 < best) { best = v1; paint = m1; }
        if (v2 < best) { best = v2; paint = m2; }
        if (v3 < best) { best = v3; paint = m3; }
        c = mix(orig, paint, uStrength);
      } else {
        // Wyostrzenie (unsharp mask): detale tekstur i krawędzie brył są wyraźniejsze.
        vec3 blur = (texture2D(tDiffuse, vUv + vec2(uTexel.x, 0.0)).rgb + texture2D(tDiffuse, vUv - vec2(uTexel.x, 0.0)).rgb
          + texture2D(tDiffuse, vUv + vec2(0.0, uTexel.y)).rgb + texture2D(tDiffuse, vUv - vec2(0.0, uTexel.y)).rgb) * 0.25;
        c = max(orig + (orig - blur) * uSharpen, vec3(0.0));
      }

      // Kontury: gradient jasności (Sobel) przyciemnia krawędzie brył.
      float tl = luma(texture2D(tDiffuse, vUv + vec2(-1.0, 1.0) * uTexel * 1.5).rgb);
      float tr = luma(texture2D(tDiffuse, vUv + vec2(1.0, 1.0) * uTexel * 1.5).rgb);
      float bl = luma(texture2D(tDiffuse, vUv + vec2(-1.0, -1.0) * uTexel * 1.5).rgb);
      float br = luma(texture2D(tDiffuse, vUv + vec2(1.0, -1.0) * uTexel * 1.5).rgb);
      float edge = length(vec2(tr + br - tl - bl, tl + tr - bl - br));
      c *= 1.0 - uInk * smoothstep(0.08, 0.45, edge);

      gl_FragColor = vec4(c, 1.0);
    }`,
};

class PaintPass extends ShaderPass {
  constructor() {
    super(PaintShader);
  }

  override setSize(width: number, height: number): void {
    (this.uniforms.uTexel!.value as Vector2).set(1 / width, 1 / height);
  }
}

/** Jeden canvas i jeden renderer WebGL dla całej gry — sceny (obóz, wyprawa) się przełącza. */
/**
 * AO liczone tylko z brył. Sprite'y (poświaty, paski HP, liczby) i przezroczyste efekty nie trafiają do bufora głębi AO —
 * inaczej wokół nich pojawiają się ciemne prostokąty. Obiekty z userData.noAo (trawa) też nie: szum AO jest przyklejony
 * do pikseli ekranu i na tysiącach cienkich źdźbeł dawał kropki, które jechały po mapie razem z kamerą.
 */
class SolidAoPass extends GTAOPass {
  // Nazwa metody z three r186 (typy @types/three jeszcze jej nie znają).
  _overrideVisibility(): void {
    const cache = (this as unknown as { _visibilityCache: Object3D[] })._visibilityCache;
    this.scene.traverse((o) => {
      const m = (o as Mesh).material as Material | undefined;
      const hide =
        (o as Sprite).isSprite || (o as Points).isPoints || (o as Line).isLine || (!!m && !Array.isArray(m) && m.transparent) || !!o.userData.noAo;
      if (hide && o.visible) {
        o.visible = false;
        cache.push(o);
      }
    });
  }
}

export class Stage {
  readonly renderer: WebGLRenderer;
  private readonly composer: EffectComposer;
  private readonly renderPass: RenderPass;
  private readonly bloom: UnrealBloomPass;
  private readonly gradePass: ShaderPass;
  private readonly paintPass: PaintPass;
  private aoPass: SolidAoPass | null = null;
  private active: StageScene | null = null;
  private readonly envMap: Texture;

  constructor(parent: HTMLElement) {
    this.renderer = new WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFShadowMap;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    parent.appendChild(this.renderer.domElement);

    // Delikatna mapa otoczenia: metal i połysk odbijają "pokój" zamiast czerni.
    const pmrem = new PMREMGenerator(this.renderer);
    this.envMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    // Wygładzanie krawędzi przez multisampling w buforze postprocessingu.
    const target = new WebGLRenderTarget(1, 1, { type: HalfFloatType, samples: 4 });
    this.composer = new EffectComposer(this.renderer, target);
    this.renderPass = new RenderPass(null as unknown as Scene, null as unknown as Camera);
    this.bloom = new UnrealBloomPass(new Vector2(1, 1), 0.45, 0.55, 0.92);
    this.gradePass = new ShaderPass(GradeShader);
    this.paintPass = new PaintPass();
    this.applyStyle();
    this.composer.addPass(this.renderPass);
    this.composer.addPass(new ShaderPass(SanitizeShader));
    this.composer.addPass(this.paintPass);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());
    this.composer.addPass(this.gradePass);

    window.addEventListener('resize', () => this.resize());
    onSettings(() => {
      this.applyStyle();
      this.setupAo();
      this.resize();
    });
    this.watchPixelRatio();
    this.resize();
  }

  private applyStyle(): void {
    const style = settings.style;
    this.paintPass.enabled = style !== 'czysty';
    const u = this.paintPass.uniforms;
    u.uMode!.value = style === 'malowany' ? 2 : 1;
    u.uInk!.value = style === 'malowany' ? 0.35 : 0.22;
  }

  /** Cienie otoczenia dla aktywnej sceny. Pass trzyma scenę i kamerę, więc przy zmianie sceny tworzymy nowy. */
  private setupAo(): void {
    if (this.aoPass) {
      this.composer.removePass(this.aoPass);
      this.aoPass.dispose();
      this.aoPass = null;
    }
    if (!settings.ao || !this.active) return;
    const ao = new SolidAoPass(this.active.scene, this.active.camera as PerspectiveCamera, window.innerWidth, window.innerHeight);
    ao.updateGtaoMaterial({ radius: 0.55, distanceExponent: 1.2, thickness: 1.5, scale: 1, samples: 16 });
    ao.blendIntensity = 0.9;
    this.composer.insertPass(ao, 1);
    this.aoPass = ao;
  }

  /**
   * Rozdzielczość renderu. Wysoka jakość: natywna gęstość ekranu, ale nie mniej niż 1,5 —
   * na zwykłych monitorach działa jak supersampling. Normalna: natywna, max 1,5 (lżej dla GPU).
   */
  private pixelRatio(): number {
    return settings.highQuality ? Math.min(2, Math.max(window.devicePixelRatio, 1.5)) : Math.min(1.5, window.devicePixelRatio);
  }

  /** Przeniesienie okna na inny monitor albo zoom zmienia devicePixelRatio. */
  private watchPixelRatio(): void {
    const mq = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    mq.addEventListener('change', () => {
      this.resize();
      this.watchPixelRatio();
    }, { once: true });
  }

  show(scene: StageScene): void {
    this.active = scene;
    this.renderPass.scene = scene.scene;
    scene.scene.environment = this.envMap;
    scene.scene.environmentIntensity = 0.12;
    this.renderPass.camera = scene.camera;
    this.setupAo();
    const g = scene.grade ?? DEFAULT_GRADE;
    const u = this.gradePass.uniforms;
    u.uSaturation!.value = g.saturation;
    u.uContrast!.value = g.contrast;
    u.uWarmth!.value = g.warmth;
    u.uVignette!.value = g.vignette;
    this.bloom.strength = g.bloom;
    if (scene.rim) {
      RIM.color.value.set(scene.rim.color);
      // Połowa siły ze strefy: przy pełnej postać w zbroi wyglądała, jakby świeciła, zamiast być po prostu czytelna.
      RIM.strength.value = scene.rim.strength * 0.5;
    }
    this.resize();
  }

  private resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const pr = this.pixelRatio();
    this.renderer.setPixelRatio(pr);
    this.renderer.setSize(w, h);
    this.composer.setPixelRatio(pr);
    this.composer.setSize(w, h);
    this.active?.resize(w, h);
  }

  frame(dt: number): void {
    if (!this.active) return;
    this.active.update(dt);
    this.composer.render(dt);
  }
}
